import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import KeyValueTable from "./KeyValueTable";
import { structuredValue, toKeyValueRows } from "./utils";

import { markdownComponents } from "@/components/VibeChat/markdownComponents";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export type IoFormat = "text" | "markdown" | "json";

const FORMAT_LABELS: Record<IoFormat, string> = {
  text: "Text",
  markdown: "Markdown",
  json: "JSON",
};

const FORMAT_ORDER: IoFormat[] = ["text", "markdown", "json"];

// Structured values (object/array/JSON string) default to the table; plain text to Text
export const detectIoFormat = (value: unknown): IoFormat =>
  structuredValue(value).structured ? "json" : "text";

export const IoFormatSelect = ({
  value,
  onChange,
}: {
  value: IoFormat;
  onChange: (format: IoFormat) => void;
}) => (
  <Select value={value} onValueChange={next => onChange(next as IoFormat)}>
    <SelectTrigger className="h-7 w-auto gap-1 border-gray-200 px-2 py-0 text-xs font-medium text-gray-600 shadow-none">
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      {FORMAT_ORDER.map(format => (
        <SelectItem key={format} value={format} className="text-xs">
          {FORMAT_LABELS[format]}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export const IoValueBody = ({
  value,
  format,
  variableChips = false,
}: {
  value: unknown;
  format: IoFormat;
  variableChips?: boolean;
}) => {
  if (format === "json") {
    return <KeyValueTable rows={toKeyValueRows(value)} variableChips={variableChips} />;
  }

  const { text } = structuredValue(value);

  if (format === "markdown") {
    return (
      <div className="prose prose-sm max-w-none rounded-lg border border-gray-200 px-3 py-2 prose-pre:overflow-x-auto">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {text}
        </ReactMarkdown>
      </div>
    );
  }

  return (
    <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-gray-200 bg-gray-50/50 p-3 font-mono text-xs text-gray-700">
      {text}
    </pre>
  );
};
